import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import { REST_API, pluginCode } from 'drapcode-constant';
import * as actions from './redux/actions';
import * as commonActions from '../common/redux/actions';
import CreateFormForPlugin from '../../common/formElements/CreateFormForPlugin';
import CustomLoader from '../common/CustomLoader';
import { drapcodeIcon } from '../../common/custom/icon-library';
import ParentDocCard from '../common/ParentDocCard';
import ParentContentCard from '../common/ParentContentCard';
import CancelButton from '../../common/ui/CancelButton';
import PluginDescription from './PluginDescription';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import PluginInstallTableHeader from './PluginInstallTableHeader';
import {
  checkPluginPermissions,
  createSchemaForPluginConfiguration,
  prepareValidationForPluginConfiguration,
  showInstallationAlert,
} from './utils';
import ShowPluginDocs from './docs/ShowPluginDocs';
import { btn } from '../../common/ui/button-constants';
import { isProjectReadOnly, snakeCaseToTitleCase } from '../../common/utils/api_utils';

export class InstallPlugin extends Component {
  static propTypes = {
    plugin: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
  };

  state = { isConfigCorrect: false };
  componentDidMount() {
    const { match, actions } = this.props;
    const { projectId, pluginId, version } = match.params;
    actions.loadPluginFields({ uuid: pluginId, version, projectId });
    actions.loadRoles({ projectId, version });
    actions.loadProjectDetail({ projectId, version });
    actions.loadAllInstalledPlugin({ projectId, version });
  }

  installAndRedirect = (formData) => {
    const { match, actions, history } = this.props;
    const { projectId, version } = match.params;

    actions.installPlugin(formData).then((res) => {
      history.push(`/plugin/${version}/v/${projectId}`);
    });
  };

  handleSubmit = (values, resetForm) => {
    const { match, actions, history, plugin } = this.props;
    const { projectId, pluginId, version } = match.params;
    const {
      pluginField: { code },
    } = plugin;
    const formData = {
      pluginUuid: pluginId,
      keystore: JSON.stringify(values),
      isEnabled: true,
      projectId: projectId,
      version,
    };

    let infoMsgHtml = '';
    showInstallationAlert(code, formData, resetForm, this.installAndRedirect);
  };

  createSubmitButton = (title, installPluginPending, handleSubmit, isReadOnly) => {
    return (
      <button
        type="button"
        className={btn.theme.medium}
        title={isReadOnly ? 'Read-Only Mode' : `Install ${title}`}
        onClick={handleSubmit}
        disabled={isReadOnly}
      >
        <i className={`wb ${drapcodeIcon('install')}`}></i>
        &nbsp;Install Plugin&nbsp;
        <CustomLoader loading={installPluginPending} />
      </button>
    );
  };

  showSubmitButton = (handleSubmit, values, isReadOnly) => {
    const { plugin, match } = this.props;
    const { isConfigCorrect } = this.state;
    const { pluginField, installPluginPending } = plugin;
    const { code, hasVariables, fields } = pluginField;

    let requiredFields =
      hasVariables && fields && Object.keys(fields).length
        ? Object.entries(fields).map(([key, value]) => {
            if (value.required) return key;
          })
        : '';
    requiredFields =
      requiredFields && requiredFields.length && requiredFields.filter((field) => !!field);
    let filledRequiredFields =
      requiredFields && requiredFields.filter((requiredField) => values[requiredField]);
    let hasAllRequiredValues = hasVariables
      ? requiredFields &&
        filledRequiredFields &&
        requiredFields.length === filledRequiredFields.length
      : true;

    const title = code === REST_API ? 'Rest API' : snakeCaseToTitleCase(code);
    if (code === pluginCode.MULTI_TENANT_SAAS) {
      if (hasAllRequiredValues) {
        return this.createSubmitButton(title, installPluginPending, handleSubmit, isReadOnly);
      } else {
        return (
          <button
            type="button"
            className={btn.default.medium}
            title={isReadOnly ? 'Read-Only Mode' : `Install ${title}`}
            disabled={!hasAllRequiredValues || isReadOnly}
          >
            <i className={`wb ${drapcodeIcon('install')}`}></i>
            &nbsp;Install Plugin&nbsp;
            <CustomLoader loading={installPluginPending} />
          </button>
        );
      }
    } else {
      return this.createSubmitButton(title, installPluginPending, handleSubmit, isReadOnly);
    }
  };

  handleCancel = () => {
    const { projectId, version } = this.props.match.params;
    this.props.history.push(`/plugin/${version}/v/${projectId}`);
  };

  showInstallationCard = () => {
    const { plugin, match, common } = this.props;
    const { projectDetail } = common;
    const { pluginField, pluginDetail } = plugin;
    const { name, fields, code, description } = pluginField;
    const isAllowed = checkPluginPermissions(code);
    if (!isAllowed) {
      return (
        <div className="d-flex align-items-center justify-content-center">
          <strong>To use this plugin, you need to upgrade your account. &nbsp;</strong>
          <a
            href={`${process.env.REACT_APP_ORGANIZATION}accounts`}
            className="btn btn-sm btn-theme-submit"
          >
            <i className="wb webconnect-paper-plane-fill"></i> upgrade
          </a>
        </div>
      );
    }
    if (!name) {
      return '';
    }
    const { uuid: projectId } = projectDetail;
    const isReadOnly = isProjectReadOnly(projectId);
    return (
      <>
        <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
          <PluginDescription code={code} description={description} projectDetail={projectDetail} />
        </div>
        <hr className={code === 'AWS_S3' ? 'mb-2' : 'mb-5'} />
        <Formik
          initialValues={createSchemaForPluginConfiguration(fields, '', pluginDetail)}
          validationSchema={prepareValidationForPluginConfiguration(fields)}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            !isReadOnly && this.handleSubmit(values, resetForm);
            setSubmitting(false);
          }}
        >
          {({ handleSubmit, values, setFieldValue, setFieldTouched }) => (
            <Form className="form-horizontal">
              <table className="table table-hover table-stripped table-bordered mb-4">
                <PluginInstallTableHeader />
                {fields && !_.isEmpty(fields) ? (
                  this.showFormForFields(setFieldValue, setFieldTouched, values)
                ) : (
                  <tbody>
                    <tr className="bg-transparent">
                      <td>
                        <h4>
                          <i className={`wb ${drapcodeIcon('info')}`}></i> Please click on the
                          Install Plugin button.
                        </h4>
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
              <div className="row">
                <div className="col-md-6">
                  <CancelButton
                    text="Cancel"
                    title="Cancel and go back to plugins"
                    onClick={this.handleCancel}
                  />
                </div>
                <div className="col-md-6 text-right">
                  {this.showSubmitButton(handleSubmit, values, isReadOnly)}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </>
    );
  };

  showFormForFields = (setFieldValue, setFieldTouched, values) => {
    const {
      match,
      plugin,
      common,
      home: { events, pages, collections },
    } = this.props;
    const { projectDetail } = common;
    const { pluginField, roles, pluginDetail, installedPlugins } = plugin;
    const { fields, code } = pluginField;
    const isTenantPlugin = installedPlugins.find(
      (plugin) => plugin.code === pluginCode.MULTI_TENANT_SAAS,
    );
    let tenantCollection = null;
    if (isTenantPlugin) {
      //Load Tenant Collection
      tenantCollection = collections.find((collection) => collection.isTenantCollection === true);
    }
    return (
      <tbody>
        {Object.keys(fields).map((key, index) => (
          <CreateFormForPlugin
            key={key}
            name={key}
            field={fields[key]}
            code={code}
            events={events}
            pages={pages}
            roles={roles}
            collections={collections}
            projectDetail={projectDetail}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            isTenantPlugin={isTenantPlugin}
            tenantCollection={tenantCollection}
            pluginData={createSchemaForPluginConfiguration(fields, '', pluginDetail)}
            values={values}
            installedPlugins={installedPlugins}
          />
        ))}
      </tbody>
    );
  };

  render() {
    const { plugin } = this.props;
    const { pluginField } = plugin;
    const { code, name } = pluginField;
    const title = (
      <>
        {name}
        {code === pluginCode.LOGIN ? <small> - User Collection Fields</small> : ''}
      </>
    );
    return (
      <CommonPageComponent>
        <div className="row mx-auto">
          <ParentContentCard icon="plugins" title={title}>
            {this.showInstallationCard()}
          </ParentContentCard>
          <ParentDocCard>
            <ShowPluginDocs code={code} />
          </ParentDocCard>
        </div>
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    plugin: state.plugin,
    home: state.home,
    common: state.common,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions, ...commonActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(InstallPlugin);
